import {
  MODEL_EFFORTS,
  type ModelChoice,
  ModelEffort,
  STUDIO_MODEL_LABELS,
  STUDIO_MODELS,
  StudioModel,
} from "@curriculum-studio/core";
import { Either, Schema } from "effect";

// CON-3203 (ADR-0154): the Expert's model/effort pick, shared by the chat composer and the generate
// form. "Default" on either select leaves that half unset, so the server keeps its own default
// (Opus 5.5 for chat; each pipeline's configured model) and the model keeps its own default effort.

const DEFAULT = "";

const EFFORT_LABELS: Readonly<Record<ModelEffort, string>> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  xhigh: "Extra high",
  max: "Max",
};

const decodeModel = Schema.decodeUnknownEither(StudioModel);
const decodeEffort = Schema.decodeUnknownEither(ModelEffort);

const SELECT = "rounded border border-zinc-200 bg-white px-1 py-0.5 text-xs text-zinc-700";

export const ModelPicker = ({
  choice,
  onChange,
  disabled = false,
  hint,
}: {
  readonly choice: ModelChoice;
  readonly onChange: (next: ModelChoice) => void;
  readonly disabled?: boolean;
  // A one-line note on when the pick applies (e.g. "Applies to new chats").
  readonly hint?: string;
}) => (
  <div
    className="flex flex-wrap items-center gap-2 text-xs text-zinc-500"
    data-testid="model-picker"
  >
    <label className="flex items-center gap-1">
      Model
      <select
        aria-label="Model"
        className={SELECT}
        disabled={disabled}
        value={choice.model ?? DEFAULT}
        onChange={(event) => {
          const decoded = decodeModel(event.target.value);
          const { model: _dropped, ...rest } = choice;
          onChange(Either.isRight(decoded) ? { ...rest, model: decoded.right } : rest);
        }}
      >
        <option value={DEFAULT}>Default</option>
        {STUDIO_MODELS.map((model) => (
          <option key={model} value={model}>
            {STUDIO_MODEL_LABELS[model]}
          </option>
        ))}
      </select>
    </label>
    <label className="flex items-center gap-1">
      Effort
      <select
        aria-label="Effort"
        className={SELECT}
        disabled={disabled}
        value={choice.effort ?? DEFAULT}
        onChange={(event) => {
          const decoded = decodeEffort(event.target.value);
          const { effort: _dropped, ...rest } = choice;
          onChange(Either.isRight(decoded) ? { ...rest, effort: decoded.right } : rest);
        }}
      >
        <option value={DEFAULT}>Default</option>
        {MODEL_EFFORTS.map((effort) => (
          <option key={effort} value={effort}>
            {EFFORT_LABELS[effort]}
          </option>
        ))}
      </select>
    </label>
    {hint !== undefined ? <span className="text-zinc-400">{hint}</span> : null}
  </div>
);
